/**
 * 서버 컴포넌트용 SSR 프리페치 유틸리티
 *
 * 'use client' 없음 — 서버에서 실행
 * features 레이어: shared, lib, workspace-pkg import 가능
 *
 * Wave 1: factory 기반 단일화 (standalone 함수 제거에 따른 변경)
 */

import type { QueryClient } from "@tanstack/react-query";

import { createApiClient } from "@/shared/api/client";
import { createProductService, type GetProductsParams } from "@/shared/api/services/productService";

import { productDetailKeys, productKeys } from "@/features/shopping/queries/keys";

/** features → config 직접 import 불가 (ESLint boundaries), 환경변수 직접 참조 */
const API_BASE_URL =
  process.env.BACKEND_PROXY_URL ??
  process.env.NEXT_PUBLIC_BACKEND_API_URL ??
  "https://dangilmarket.com/api/v1";

/** 공개 API용 임시 클라이언트 생성 */
function createTempClient() {
  return createApiClient({
    baseUrl: API_BASE_URL,
    getToken: () => null,
    onUnauthorized: () => {},
  });
}

/**
 * 상품 목록 SSR 프리페치
 *
 * 공개 API (인증 불필요) 용도로 no-auth 임시 클라이언트를 생성한다.
 */
export async function prefetchProducts(
  queryClient: QueryClient,
  params?: GetProductsParams,
): Promise<void> {
  const service = createProductService(createTempClient());

  await queryClient.prefetchQuery({
    queryKey: productKeys.list(params),
    queryFn: () =>
      service.getProducts(params as Record<string, string | number | boolean | undefined>),
  });
}

/**
 * 상품 상세 SSR 프리페치
 */
export async function prefetchProductDetail(queryClient: QueryClient, id: string): Promise<void> {
  const service = createProductService(createTempClient());

  await queryClient.prefetchQuery({
    queryKey: productDetailKeys.detail(id),
    queryFn: () => service.getProductDetail(id),
  });
}
